import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../common/Dialog';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { subscriptionService } from '../../services/subscriptionService';
import { X } from 'lucide-react';
import { getSubscriptionConfig, getServiceIcon, genericIcons } from '../../config/subscriptionIcons';
import { subscriptionConfigs } from '../../config/subscriptionData';
import { ToggleSwitch } from '../common/Switch';
import { getSubscriptionIcon } from '../../utils/iconUtils';
import { useAuth } from '../../context/AuthContext';
import { CURRENCIES } from '../../config/currencies';
import api from '../../config/api';
import endpoints from '../../config/endpoints';

const SUBSCRIPTION_CATEGORIES = [
    'streaming',
    'music',
    'gaming',
    'productivity',
    'cloud',
    'business',
    'creative',
    'education',
    'fitness',
    'wellness',
    'security',
    'shopping',
    'dating',
    'entertainment',
    'sports',
    'news',
    'other'
];

const SUBSCRIPTION_TYPES = [
    'daily',
    'monthly',
    'yearly',
    'quarterly'
];

const SUBSCRIPTION_STATUSES = [
    'active',
    'paused',
    'cancelled'
];

const formatDateToUTC = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('.')[0] + 'Z';
};

const getTodayFormatted = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

const getNextBillingDate = (startDate, subscriptionType) => {
    const initialDate = new Date(startDate);
    const today = new Date();
    let nextDate = new Date(startDate);

    // First calculate one period from the start date
    switch (subscriptionType) {
        case 'yearly':
            nextDate.setFullYear(initialDate.getFullYear() + 1);
            break;
        case 'quarterly':
            nextDate.setMonth(initialDate.getMonth() + 3);
            break;
        case 'daily':
            nextDate.setDate(initialDate.getDate() + 1);
            break;
        default: // monthly
            nextDate.setMonth(initialDate.getMonth() + 1);
    }

    // If that date is still in the past, keep adding periods until we get a future date
    while (nextDate < today) {
        switch (subscriptionType) {
            case 'yearly':
                nextDate.setFullYear(nextDate.getFullYear() + 1);
                break;
            case 'quarterly':
                nextDate.setMonth(nextDate.getMonth() + 3);
                break;
            case 'daily':
                nextDate.setDate(nextDate.getDate() + 1);
                break;
            default: // monthly
                nextDate.setMonth(nextDate.getMonth() + 1);
        }
    }

    return nextDate.toISOString().split('T')[0];
};

export const AddSubscriptionModal = ({ isOpen, onClose, onSuccess, subscription }) => {
    const { user, updateUserProfile } = useAuth();
    const { trackEvent, events } = useAnalytics();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCustomService, setIsCustomService] = useState(false);

    const defaultConfig = subscriptionConfigs[0];

    const [formData, setFormData] = useState({
        service_name: "",
        category: "",
        subscription_type: 'monthly',
        start_date: getTodayFormatted(),
        next_billing_date: getNextBillingDate(getTodayFormatted(), 'monthly'),
        cost: defaultConfig.defaultPrice.toString(),
        currency: user?.preferences?.currency || 'USD',
        status: 'active',
        icon: defaultConfig.id,
    });

    useEffect(() => {
        if (subscription) {
            const isKnownService = subscriptionConfigs.some(
                config => config.id.toLowerCase() === subscription.service_name.toLowerCase()
            );
            setIsCustomService(!isKnownService);
            setFormData({
                ...subscription,
                start_date: new Date(subscription.start_date).toISOString().split('T')[0],
                next_billing_date: new Date(subscription.next_billing_date).toISOString().split('T')[0],
                cost: subscription.cost.toString(),
            });
        } else {
            const today = getTodayFormatted();
            setIsCustomService(false);
            setFormData({
                service_name: defaultConfig.name,
                category: defaultConfig.category,
                subscription_type: 'monthly',
                start_date: today,
                next_billing_date: getNextBillingDate(today, 'monthly'),
                cost: defaultConfig.defaultPrice.toString(),
                currency: user?.preferences?.currency || 'USD',
                status: 'active',
                icon: defaultConfig.id,
            });
        }
    }, [subscription, isOpen, user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleIconChange = (iconId) => {
        setFormData(prev => ({
            ...prev,
            icon: iconId
        }));
    };

    const handleServiceSelect = (serviceId) => {
        const config = subscriptionConfigs.find(config =>
            config.id.toLowerCase() === serviceId.toLowerCase()
        );

        if (!config) {
            console.warn('No config found for serviceId:', serviceId);
            return;
        }

        trackEvent(events.FEATURE.SUBSCRIPTION_ADD, 'feature', {
            action: 'select_service',
            service_id: serviceId,
            is_custom: isCustomService
        });

        const updatedFormData = {
            ...formData,
            service_name: config.name,
            category: config.category || 'other',
            subscription_type: config.defaultPlan || 'monthly',
            next_billing_date: getNextBillingDate(
                formData.start_date,
                config.defaultPlan || 'monthly'
            ),
            icon: config.id
        };

        setFormData(updatedFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const finalNextBillingDate = getNextBillingDate(
                formData.start_date,
                formData.subscription_type
            );

            const subscriptionData = {
                ...formData,
                cost: parseFloat(formData.cost),
                start_date: formatDateToUTC(formData.start_date),
                next_billing_date: formatDateToUTC(finalNextBillingDate),
            };

            let response;
            if (subscription) {
                response = await subscriptionService.updateSubscription(subscription.id, subscriptionData);
                trackEvent(events.FEATURE.SUBSCRIPTION_ADD, 'feature', {
                    action: 'update',
                    subscription_id: subscription.id,
                    service_name: formData.service_name,
                    subscription_type: formData.subscription_type,
                    currency: formData.currency,
                    status: formData.status,
                    is_custom: isCustomService
                });
            } else {
                response = await subscriptionService.addSubscription(subscriptionData);
                trackEvent(events.FEATURE.SUBSCRIPTION_ADD, 'feature', {
                    action: 'create',
                    service_name: formData.service_name,
                    subscription_type: formData.subscription_type,
                    currency: formData.currency,
                    status: formData.status,
                    is_custom: isCustomService
                });

                // Update user's currency preference if it's different
                if (formData.currency !== user?.preferences?.currency) {
                    try {
                        const requestPayload = {
                            ...user.preferences,
                            currency: formData.currency,
                        };
                        await api.put(endpoints.user.updatePreferences, requestPayload);
                        await updateUserProfile();
                    } catch (err) {
                        console.error('Failed to update currency preference:', err);
                    }
                }
            }

            onSuccess();
            onClose();
        } catch (err) {
            trackEvent(events.SYSTEM.ERROR, 'system', {
                action: subscription ? 'update_subscription' : 'create_subscription',
                error: err.message,
                service_name: formData.service_name,
                is_custom: isCustomService
            });

            console.error('Error details:', {
                message: err.message,
                response: err.response,
                stack: err.stack
            });

            setError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                err.message ||
                `Failed to ${subscription ? 'update' : 'add'} subscription`
            );
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        const today = getTodayFormatted();
        setFormData({
            service_name: defaultConfig.name,
            category: defaultConfig.category,
            subscription_type: 'monthly',
            start_date: today,
            next_billing_date: getNextBillingDate(today, 'monthly'),
            cost: defaultConfig.defaultPrice.toString(),
            currency: defaultConfig.currency,
            status: 'active',
            icon: defaultConfig.id,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] bg-white border border-gray-200 shadow-lg max-h-[90vh] overflow-y-auto p-4">
                <DialogHeader className="sticky top-0 bg-white z-10 pb-4">
                    <DialogTitle className="text-gray-900 text-xl font-semibold">
                        {subscription ? 'Edit Subscription' : 'Add New Subscription'}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pb-4">
                    {error && (
                        <div className="text-red-500 text-sm p-3 bg-red-50 rounded-md border border-red-200">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                            Custom Service
                        </label>
                        <ToggleSwitch
                            checked={isCustomService}
                            onCheckedChange={setIsCustomService}
                            aria-label="Toggle custom service input"
                        />
                    </div>

                    {isCustomService && (
                        <>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Service Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    name="service_name"
                                    value={formData.service_name}
                                    onChange={handleChange}
                                    placeholder="Enter service name"
                                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Category
                                </label>
                                <div className="max-h-48 overflow-y-auto">
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-2 border rounded-md bg-gray-50">
                                        {genericIcons.map(({ id, icon: IconComponent, label }) => (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        category: id,
                                                        icon: id
                                                    }));
                                                }}
                                                className={`p-2 rounded-md hover:bg-gray-200 transition-colors flex flex-col items-center gap-1 ${
                                                    formData.category === id ? 'bg-gray-200 ring-2 ring-blue-500' : ''
                                                }`}
                                                aria-label={`Select ${label} category`}
                                            >
                                                <IconComponent className="h-5 w-5 text-gray-700" />
                                                <span className="text-xs text-gray-600">{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {!isCustomService && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                                Service Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="service_name"
                                    value={formData.service_name ?
                                        subscriptionConfigs.find(config =>
                                            config.name.toLowerCase() === formData.service_name.toLowerCase()
                                        )?.id || '' :
                                        ''
                                    }
                                    onChange={(e) => handleServiceSelect(e.target.value)}
                                    className="w-full rounded-md border border-gray-200 bg-gray-50 pl-10 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                    required
                                >
                                    <option value="">Select a service</option>
                                    {subscriptionConfigs
                                        .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
                                        .map(config => (
                                            <option key={config.id} value={config.id}>
                                                {config.name}
                                            </option>
                                        ))}
                                </select>
                                {formData.service_name && (
                                    <div className="absolute left-3 top-2">
                                        {(() => {
                                            const Icon = getSubscriptionIcon(formData.service_name, formData.category);
                                            return <Icon className="h-5 w-5 text-gray-500" />;
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                                Subscription Type
                            </label>
                            <select
                                name="subscription_type"
                                value={formData.subscription_type}
                                onChange={handleChange}
                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                required
                            >
                                {SUBSCRIPTION_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                                Start Date
                            </label>
                            <Input
                                name="start_date"
                                type="date"
                                value={formData.start_date}
                                onChange={handleChange}
                                required
                                className="bg-gray-50 border-gray-200 focus:border-blue-500"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                                Cost <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                <div className="w-1/2">
                                    <select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleChange}
                                        className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                        required
                                    >
                                        {CURRENCIES.map(currency => (
                                            <option key={currency.code} value={currency.code}>
                                                {currency.code} ({currency.symbol})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="relative flex-1">
                                    <Input
                                        name="cost"
                                        type="number"
                                        step="0.01"
                                        value={formData.cost}
                                        onChange={handleChange}
                                        placeholder="15.99"
                                        required
                                        className="bg-gray-50 border-gray-200 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            Status
                        </label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            {SUBSCRIPTION_STATUSES.map(status => (
                                <option key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <DialogFooter className="sticky bottom-0 bg-white z-10 gap-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="bg-white border-gray-300 hover:bg-gray-50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {isLoading ?
                                (subscription ? 'Updating...' : 'Adding...') :
                                (subscription ? 'Update Subscription' : 'Add Subscription')
                            }
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}; 